/**
 * Header behaviour: the Shop mega menu, the rotating announcement, the rotating
 * search placeholder, and the mobile disclosure.
 *
 * All four are progressive. Shop is a real link to /collections, the first
 * announcement message and the first placeholder are rendered server-side, and
 * every link in the mega menu also exists in the mobile nav's plain markup. If
 * this file never loads, the header is a static header that works.
 *
 * The mega menu opens on hover of Shop and closes when the pointer leaves the
 * whole header region — not when it leaves Shop. Closing on Shop's own
 * mouseleave would shut the panel the moment the pointer crossed the header's
 * bottom padding on its way down into it, which is the classic reason a mega
 * menu feels like it is fighting you.
 */

const OPEN_DELAY = 60; // ms — long enough that sweeping past Shop does nothing
const CLOSE_DELAY = 180; // ms — forgiving of a pointer that clips the edge

function mega(root) {
  const shop = root.querySelector('[data-bb-shop]');
  const panel = root.querySelector('[data-bb-mega]');
  if (!shop || !panel) return;

  let openTimer = null;
  let closeTimer = null;

  const clear = () => {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
  };

  const open = () => {
    clear();
    panel.hidden = false;
    shop.setAttribute('aria-expanded', 'true');
  };

  const close = () => {
    clear();
    panel.hidden = true;
    shop.setAttribute('aria-expanded', 'false');
  };

  shop.addEventListener('mouseenter', () => {
    clear();
    openTimer = setTimeout(open, OPEN_DELAY);
  });

  // Leaving Shop only arms the close; entering the panel disarms it again.
  shop.addEventListener('mouseleave', () => {
    clearTimeout(openTimer);
    closeTimer = setTimeout(close, CLOSE_DELAY);
  });
  panel.addEventListener('mouseenter', clear);
  root.addEventListener('mouseleave', close);

  // Keyboard: the panel opens when focus reaches Shop and stays open while
  // focus is anywhere inside it, so the links are reachable by tab alone.
  shop.addEventListener('focus', open);
  root.addEventListener('focusout', (e) => {
    if (!root.contains(e.relatedTarget)) close();
  });
  root.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || panel.hidden) return;
    close();
    shop.focus();
  });

  // A tap on a touch device should open the panel rather than jump straight to
  // /collections — the same tap that a mouse would have used to hover.
  shop.addEventListener('click', (e) => {
    if (!window.matchMedia('(hover: none)').matches) return;
    if (panel.hidden) {
      e.preventDefault();
      open();
    }
  });
}

/** Crossfades between the announcement messages. One message: nothing to do. */
function announcement(root) {
  const bar = root.querySelector('[data-bb-announce]');
  if (!bar) return;
  const messages = [...bar.querySelectorAll('.bb-announce__msg')];
  if (messages.length < 2) return;

  const interval = Number(bar.dataset.interval) || 4500;
  let i = 0;
  setInterval(() => {
    messages[i].classList.remove('is-on');
    i = (i + 1) % messages.length;
    messages[i].classList.add('is-on');
  }, interval);
}

/**
 * Cycles the search placeholder, but only while the field is empty and nobody
 * is using it. Changing the placeholder under someone who is mid-thought about
 * what to type is the one way this idea goes wrong.
 */
function placeholder(root) {
  const input = root.querySelector('[data-bb-search-input]');
  if (!input) return;
  const list = (input.dataset.bbPlaceholders || '')
    .split('\n')
    .map((x) => x.trim())
    .filter(Boolean);
  if (list.length < 2) return;

  const bar = root.querySelector('[data-bb-announce]');
  const interval = Number(bar?.dataset.interval) || 4500;
  let i = 0;
  setInterval(() => {
    if (input.value || document.activeElement === input) return;
    i = (i + 1) % list.length;
    input.placeholder = list[i];
  }, interval);
}

function mobile(root) {
  const burger = root.querySelector('.bb-header__burger');
  const nav = root.querySelector('.bb-mobile-nav');
  if (!burger || !nav) return;
  burger.addEventListener('click', () => {
    const open = burger.getAttribute('aria-expanded') === 'true';
    burger.setAttribute('aria-expanded', String(!open));
    nav.hidden = open;
  });
}

function init(root) {
  if (!root || root.dataset.bbHeaderReady) return;
  root.dataset.bbHeaderReady = '1';
  mega(root.querySelector('[data-bb-header]') || root);
  announcement(root);
  placeholder(root);
  mobile(root);
}

document.querySelectorAll('.bb-header-root').forEach(init);

// The theme editor re-renders the section on every settings change, which
// replaces the DOM this was bound to.
document.addEventListener('shopify:section:load', (e) => {
  e.target.querySelectorAll('.bb-header-root').forEach(init);
});
